import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { get } from '@truefit/http-utils';

// Define the camera types based on the backend structure
export interface GameCameraSimple {
  id: number;
  uniqueId: string;
  cameraId: number;
  gameId: number;
  camIndex: number;
  version: number | null;

  // Camera properties
  displayName: string | null;
  positionX: number;
  positionY: number;
  positionZ: number;
  forwardX: number;
  forwardY: number;
  forwardZ: number;
  tilt: number | null;
  distMax: number | null;
  distMin: number | null;
  fovMax: number | null;
  fovMin: number | null;
  offset: number;
  isLive: boolean | null;
  isHidden: boolean;       // Per-org hide flag (server-resolved from orgHiddenCameraName rules)

  // Metadata
  createdAt: string;
  updatedAt: string;

  // Base camera info
  baseCameraInfo: {
    camIndex: number;
    hardwareId: string | null;
  };

  // Setup context
  cameraSetup: {
    id: number;
    name: string;
    units: string;
  } | null;

  // Lens info
  lens: {
    id: number;
    name: string;
    curveRadius: number[];
    curveAngle: number[];
  } | null;

  // Tracking configuration
  trackingConfig: {
    id: number;
    name: string;
    slerpRate: number;
    lerpRate: number;
    vDamp: number;
  } | null;
}

// Fetch function for game camera
export const fetchGameCamera = async (
  gameId: string,
  cameraId: number,
): Promise<GameCameraSimple> => {
  try {
    const response = await get<GameCameraSimple>(
      `/v1.0/games/${gameId}/camera?cameraId=${cameraId}`,
    );
    return response.data;
  } catch (error) {
    console.error('Failed to fetch game camera:', error);
    throw error;
  }
};

// Custom hook to fetch game camera
const useGameCameraQuery = (
  gameId?: string,
  cameraId?: number,
): UseQueryResult<GameCameraSimple, Error> => {
  return useQuery({
    queryKey: ['gameCamera', gameId, cameraId],
    queryFn: () => fetchGameCamera(gameId!, cameraId!),
    enabled: !!gameId && !!cameraId && cameraId > 0, // Only fetch if both parameters are provided and valid
    staleTime: 1000 * 60 * 30, // 30 minutes - cameras don't change often
    gcTime: 1000 * 60 * 60, // 60 minutes
    retry: 1,
    retryDelay: 1000,
  });
};

export default useGameCameraQuery;
